import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  CurrencyDollar,
  CreditCard,
  Coins,
  TrendUp,
  Gear,
  Airplane,
  Calculator,
  Target,
  Warning,
  TrendDown,
} from "@phosphor-icons/react";
import { useData } from "../context/DataContext";
import { formatCurrency } from "../utils/calculations";

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  category: "income" | "expenses" | "assets" | "analysis" | "settings";
  priority: number;
}

const QuickActions: React.FC = () => {
  const navigate = useNavigate();
  const { getDashboardData } = useData();
  const dashboardData = getDashboardData();
  const [showAllActions, setShowAllActions] = useState(false);

  // Calculate some quick metrics
  const runwayMonths = dashboardData?.runwayProjection?.[0]?.runwayMonths || 0;
  const isLowRunway = runwayMonths <= 3;

  const quickActions: QuickAction[] = [
    // High Priority Actions
    {
      id: "add-income",
      title: "Add Income",
      description: "Record new income source",
      icon: (
        <CurrencyDollar className="w-5 h-5 text-green-500" weight="duotone" />
      ),
      action: () => navigate("/income"),
      category: "income",
      priority: 1,
    },
    {
      id: "add-expense",
      title: "Add Expense",
      description: "Record new expense",
      icon: <CreditCard className="w-5 h-5 text-red-500" weight="duotone" />,
      action: () => navigate("/expenses"),
      category: "expenses",
      priority: 1,
    },
    {
      id: "add-asset",
      title: "Add Asset",
      description: "Add account or investment",
      icon: <Coins className="w-5 h-5 text-yellow-500" weight="duotone" />,
      action: () => navigate("/assets"),
      category: "assets",
      priority: 1,
    },
    {
      id: "view-runway",
      title: "View Runway",
      description: "Check financial runway",
      icon: <Airplane className="w-5 h-5 text-primary-500" weight="duotone" />,
      action: () => navigate("/"),
      category: "analysis",
      priority: 1,
    },

    // Medium Priority Actions
    {
      id: "manage-income",
      title: "Manage Income",
      description: "View and edit income sources",
      icon: <CurrencyDollar className="w-5 h-5 text-green-400" />,
      action: () => navigate("/income"),
      category: "income",
      priority: 2,
    },
    {
      id: "manage-expenses",
      title: "Manage Expenses",
      description: "View and edit expenses",
      icon: <CreditCard className="w-5 h-5 text-red-400" />,
      action: () => navigate("/expenses"),
      category: "expenses",
      priority: 2,
    },
    {
      id: "manage-assets",
      title: "Manage Assets & Debts",
      description: "View and edit accounts",
      icon: <Coins className="w-5 h-5 text-yellow-400" />,
      action: () => navigate("/assets"),
      category: "assets",
      priority: 2,
    },
    {
      id: "view-projections",
      title: "View Projections",
      description: "Financial projections",
      icon: <TrendUp className="w-5 h-5 text-blue-500" weight="duotone" />,
      action: () => navigate("/projections"),
      category: "analysis",
      priority: 2,
    },

    // Lower Priority Actions
    {
      id: "add-transaction",
      title: "Quick Transaction",
      description: "Add income or expense (Coming Soon)",
      icon: <Plus className="w-5 h-5 text-primary-500" weight="duotone" />,
      action: () => {
        // Show a modal or navigate to a quick transaction page
        alert("Quick transaction feature coming soon!");
      },
      category: "expenses",
      priority: 3,
    },
    {
      id: "runway-calculator",
      title: "Runway Calculator",
      description: "Calculate financial runway (Coming Soon)",
      icon: <Calculator className="w-5 h-5 text-purple-500" weight="duotone" />,
      action: () => {
        // Show runway calculator
        alert("Runway calculator feature coming soon!");
      },
      category: "analysis",
      priority: 3,
    },
    {
      id: "financial-goals",
      title: "Set Goals",
      description: "Set financial targets (Coming Soon)",
      icon: <Target className="w-5 h-5 text-orange-500" weight="duotone" />,
      action: () => {
        // Show goals setting
        alert("Financial goals feature coming soon!");
      },
      category: "analysis",
      priority: 3,
    },
    {
      id: "settings",
      title: "Settings",
      description: "App preferences",
      icon: <Gear className="w-5 h-5 text-gray-400" />,
      action: () => navigate("/settings"),
      category: "settings",
      priority: 3,
    },
  ];

  // Filter actions based on priority and show state
  const visibleActions = showAllActions
    ? quickActions
    : quickActions.filter((action) => action.priority <= 2);

  // Group actions by category
  const groupedActions = visibleActions.reduce((acc, action) => {
    if (!acc[action.category]) {
      acc[action.category] = [];
    }
    acc[action.category].push(action);
    return acc;
  }, {} as Record<string, QuickAction[]>);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "income":
        return <CurrencyDollar className="w-4 h-4 text-green-500" />;
      case "expenses":
        return <CreditCard className="w-4 h-4 text-red-500" />;
      case "assets":
        return <Coins className="w-4 h-4 text-yellow-500" />;
      case "analysis":
        return <TrendUp className="w-4 h-4 text-blue-500" />;
      case "settings":
        return <Gear className="w-4 h-4 text-gray-500" />;
      default:
        return <Plus className="w-4 h-4 text-gray-500" />;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "income":
        return "Income";
      case "expenses":
        return "Expenses";
      case "assets":
        return "Assets & Debts";
      case "analysis":
        return "Analysis";
      case "settings":
        return "Settings";
      default:
        return "Other";
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="card-title">Quick Actions</h3>
        {isLowRunway && (
          <div className="flex items-center space-x-2 text-orange-400">
            <Warning className="w-4 h-4" weight="duotone" />
            <span className="text-sm font-medium">Low Runway</span>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {dashboardData && (
        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="text-center">
            <p className="text-xs text-gray-600 dark:text-gray-400">Runway</p>
            <p
              className={`text-sm font-bold ${
                isLowRunway
                  ? "text-orange-400"
                  : "text-gray-900 dark:text-white"
              }`}>
              {runwayMonths === Infinity
                ? "∞"
                : `${runwayMonths.toFixed(1)} months`}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600 dark:text-gray-400">Balance</p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">
              {formatCurrency(dashboardData.currentBalance)}
            </p>
          </div>
        </div>
      )}

      {/* Dynamic Action Buttons */}
      <div className="space-y-4">
        {Object.entries(groupedActions).map(([category, actions]) => (
          <div key={category} className="space-y-2">
            <div className="flex items-center space-x-2">
              {getCategoryIcon(category)}
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {getCategoryLabel(category)}
              </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {actions.map((action) => {
                const isComingSoon = action.description.includes("Coming Soon");
                return (
                  <button
                    key={action.id}
                    onClick={action.action}
                    className={`flex items-center space-x-4 p-3 sm:p-4 rounded-lg border transition-colors ${
                      isComingSoon
                        ? "border-gray-300 dark:border-gray-500 bg-gray-50 dark:bg-gray-700 opacity-75 cursor-not-allowed"
                        : "border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer group"
                    }`}>
                    <div className="flex-shrink-0">{action.icon}</div>
                    <div className="flex-1 min-w-0 text-left">
                      <p
                        className={`text-sm font-medium ${
                          isComingSoon
                            ? "text-gray-500 dark:text-gray-400"
                            : "text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400"
                        }`}>
                        {action.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {action.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Show More/Less Toggle */}
      {quickActions.length > visibleActions.length && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={() => setShowAllActions(!showAllActions)}
            className="w-full text-center text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
            {showAllActions ? "Show Less" : "Show More Actions"}
          </button>
        </div>
      )}
    </div>
  );
};

export default QuickActions;
